import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfig } from 'app/config/service.config';
import { AuthUtils } from 'app/core/auth/auth.utils';
import {
    BehaviorSubject,
    catchError,
    map,
    Observable,
    of,
    switchMap,
    throwError,
} from 'rxjs';
import { LogService } from '../logging/log.service';
import { UserAuthenticate } from './auth.types';
import { Router } from '@angular/router';
import { BrandService } from '../brand/brand.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _userAuthenticate: BehaviorSubject<UserAuthenticate | null> =
        new BehaviorSubject(null);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _apiServer: AppConfig,
        private _logging: LogService,
        private _router: Router,
        private _brandService: BrandService
    ) {}

    private getServiceEndpoint(): string {
        return this._brandService.getApiEndpoint(this._apiServer);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     * 
     * SECURITY WARNING: Token Storage in localStorage
     * - localStorage is vulnerable to XSS attacks
     * - Consider using httpOnly cookies for enhanced security
     * - Ensure all user input is properly sanitized to prevent XSS
     * - Implement Content Security Policy (CSP) headers
     * 
     * Best Practices:
     * - Use short-lived access tokens with refresh tokens
     * - Clear tokens on logout and session expiry
     * - Never log token values
     * - Implement token rotation
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        let userService = this.getServiceEndpoint();

        return this._httpClient
            .post(userService + '/auth/sign-in', credentials)
            .pipe(
                map((response: any) => {
                    this._logging.debug(
                        'User authenticated successfully',
                        { email: credentials.email }
                    );

                    // Store the access token in the local storage
                    this.accessToken = response.data.jwt;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    this._userAuthenticate.next(response);

                    // Store the user on the user service
                    // this._userService.user = response.user;

                    // Return a new observable with the response
                    return response;
                })
            );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        let userService = this.getServiceEndpoint();
        const header = {
            // headers: new HttpHeaders().set("Authorization", this.jwt),
            withCredentials: true,
        };

        const body = {
            jwt: this.accessToken,
        };

        // Renew token
        return this._httpClient
            .post(userService + '/auth/refresh', body, header)
            .pipe(
                catchError(
                    () => this.signOutAndNavigate()
                    // Return false
                ),
                switchMap((response: any) => {
                    this._logging.debug('User token refreshed successfully');

                    // Store the access token in the local storage
                    // this.jwt = response;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    // this._userService.user = response.user;

                    // Return true
                    return of(true);
                })
            );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign out and Navigate
     */
    signOutAndNavigate(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        this._router.navigate(['/sign-out']);

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
